import dayjs from 'dayjs';
import {useSelector} from 'react-redux';
import {useEffect, useState} from 'react';
import TimedMessageRequest, {
  iTimedMsgDateRange,
} from './request/TimedMessageRequest';
import {
  ITimedMessageDayType,
  ITimedMessageMsgType,
} from '../../../dto/timed-message/TimedMessageRequest.dto';
import {IStation} from '../../../dto/Station.dto';
import {companyIdSelector} from '../../../store/Slices/companySlice';
import {iTimedMsgTimesItem} from './request/times/TimedMsgRequestTimesCard';
import {iTimedMessageMessageItem} from './request/contents/TimedMsgRequestContentMessageCard';
import {useRqViewTimedMessagesByRequestID} from '../../../react_query/messages/timed-messages/ViewTimedMsgDetails';
import {GetDaysFromDateRange} from '../../../components/utlis/date-time/GetDaysInDateRange';
import {ITimedRequestDateRange} from '../../../dto/timed-message/TimedRequestDateRange.dto';

const defaultMessages: iTimedMessageMessageItem[] = [
  {
    id: -1 * Date.now(),
    isNew: true,
    type: 'message',
    message: '',
    artist: '',
    title: '',
    label: 'Message',
  },
];

const defaultState = {
  defaultName: '',
  defaultStep: 0,
  defaultStations: [] as IStation[],
  defaultMessages,
  defaultDayType: 'no-end' as ITimedMessageDayType,
  defaultContentType: 'message' as ITimedMessageMsgType,
  defaultDateRanges: [
    {
      id: -1 * Date.now(),
      start_date: null,
      end_date: null,
      chosenDays: [],
    } as iTimedMsgDateRange,
  ],
  defaultTimeItems: [
    {
      id: -1 * Date.now(),
      availableDays: [],
      selectedDays: [],
      timeRanges: [],
    },
  ] as iTimedMsgTimesItem[],
};

const manipulateChosenDays = (dateRange: ITimedRequestDateRange) => {
  const flattenedWeeks = dateRange.day_times?.map(o => o.week_days).flat();
  return (
    (flattenedWeeks
      ?.filter((item, index, array) => array.indexOf(item) === index)
      .map(
        d => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase(),
      ) as string[]) ?? []
  );
};

const TimedMessageCreate = ({
  onClose,
  setStatus,
  timedId,
  type,
  repository,
}: {
  onClose: () => void;
  timedId?: number;
  type?: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  repository?: boolean;
}) => {
  const companyId = useSelector(companyIdSelector);
  const [state, setState] = useState({...defaultState});
  const {data, isFetching, isError} = useRqViewTimedMessagesByRequestID(
    Number(companyId),
    Number(timedId),
    Boolean(timedId) && type !== 'restore',
  );

  useEffect(() => {
    if (data && !isFetching && !isError) {
      const defaultMessages: iTimedMessageMessageItem[] =
        data.timed_sequences.map(o => {
          return {
            id: o.sequenceable_id,
            isNew: false,
            type: o.sequenceable_type === 'message' ? 'message' : 'song',
            message:
              o.sequenceable_type === 'message' ? o.sequenceable.message : '',
            artist: o.sequenceable.media_track?.track_artist ?? '',
            title: o.sequenceable.media_track?.track_name ?? '',
            label: o.sequenceable_type === 'message' ? 'Message' : 'Song',
            track: o.sequenceable.media_track,
          };
        });
      setState(prev => ({
        ...prev,
        defaultName: `${data.name} ${
          type === 'duplicate' && !repository ? 'duplicate' : ''
        }`,
        defaultStations: data.stations,
        defaultMessages: defaultMessages,
        defaultDayType: data.day_type,
        defaultContentType: data.message_type,
        defaultDateRanges: data.date_ranges.map(o => {
          const chosenDays = manipulateChosenDays(o) as string[];
          return {
            id: o.id,
            start_date: dayjs(o.start_date),
            end_date: dayjs(o.end_date),
            chosenDays,
            day_times:
              o.day_times && o.day_times.length
                ? o.day_times.map(time => {
                    return {
                      id: time.id,
                      availableDays: chosenDays,
                      selectedDays: time.week_days.map(
                        d =>
                          d.charAt(0).toUpperCase() + d.slice(1).toLowerCase(),
                      ),
                      timeRanges: time.start_times.map((t, i) => ({
                        id: i,
                        start_time: dayjs(t, 'HH:mm:ss'),
                      })) as [],
                    };
                  })
                : [],
          };
        }),
        defaultTimeItems: data.day_times.map(time => {
          const dates = data.date_ranges.find(
            o => o.id === time.timed_request_date_range_id,
          );
          const availableDays = dates
            ? (GetDaysFromDateRange(
                dayjs(dates.start_date),
                dayjs(dates.end_date),
              ) as string[])
            : [];
          return {
            id: time.id,
            availableDays,
            selectedDays: time.week_days.map(
              d => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase(),
            ),
            timeRanges: time.start_times.map((t, i) => ({
              id: i,
              start_time: dayjs(t, 'HH:mm:ss'),
            })) as [],
          };
        }),
      }));
    }
  }, [data, isError, isFetching, type, repository]);

  if (type === 'update') {
    return !isFetching && state.defaultName ? (
      <TimedMessageRequest
        onClose={onClose}
        {...state}
        updateId={timedId}
        messageStatus={data?.status ?? null}
        setStatus={setStatus}
      />
    ) : null;
  }

  if (type === 'duplicate') {
    return !isFetching && state.defaultName ? (
      <TimedMessageRequest
        onClose={onClose}
        {...state}
        isCopyForm
        messageStatus={null}
        setStatus={setStatus}
        repository={repository}
      />
    ) : null;
  }
  return (
    <TimedMessageRequest
      onClose={onClose}
      {...defaultState}
      restoreId={timedId}
      messageStatus={null}
      setStatus={setStatus}
    />
  );
};

export default TimedMessageCreate;
